import { db } from '@/shared/db/database';

export const DEBUG_TELEMETRY_KEY = 'mi_debug_telemetry';

/**
 * Module-level sync cache. Populated by load() and updated immediately on
 * save(). Defaults to false so logTiming() is silently no-op in production
 * until the database read completes (typically < 50 ms after app boot).
 */
let _cache = false;

/**
 * Listener set for reactive consumers (e.g. useDebugFlag).
 * save() notifies all listeners after updating _cache.
 * load() intentionally does NOT notify — it is a boot-time read.
 */
const _listeners = new Set<(v: boolean) => void>();

/**
 * Persists and syncs the debug-telemetry flag to db.settings.
 *
 * Usage:
 *   - main.tsx: `void debugTelemetryService.load()` right after initSyncWorker
 *   - AdminConfigPage: read via getSync(), write via save()
 *   - logTiming: gate via getSync()
 */
export const debugTelemetryService = {
  /**
   * Reads 'mi_debug_telemetry' from db.settings and warms the sync cache.
   * Call once at app boot (fire-and-forget: `void debugTelemetryService.load()`).
   */
  async load(): Promise<void> {
    const record = await db.settings.get(DEBUG_TELEMETRY_KEY);
    _cache = record?.value === 'true';
  },

  /**
   * Persists the flag to db.settings and updates the sync cache immediately
   * so subsequent getSync() calls reflect the new value without a round-trip.
   * Notifies all subscribers registered via subscribe() after cache mutation.
   */
  async save(enabled: boolean): Promise<void> {
    _cache = enabled;
    await db.settings.put({
      key:        DEBUG_TELEMETRY_KEY,
      value:      enabled ? 'true' : 'false',
      updated_at: Date.now(),
    });
    _listeners.forEach((fn) => fn(_cache));
  },

  /**
   * Returns the cached value synchronously. Safe to call in hot rendering
   * paths (logTiming, admin toggles). Returns false until load() completes.
   */
  getSync(): boolean {
    return _cache;
  },

  /**
   * Registers a listener called with the new boolean value every time save()
   * completes. Returns an unsubscribe function that removes the listener.
   *
   * Note: load() does NOT notify listeners — it is a silent boot-time read.
   */
  subscribe(fn: (v: boolean) => void): () => void {
    _listeners.add(fn);
    return () => {
      _listeners.delete(fn);
    };
  },
};
